// ActivityPulse — the live feed on the Overview dashboard. Every row is a
// real DB record (emails/messages/meetings captured + approval decisions),
// merged newest-first by loadRecentActivity. Staggered entrance; channel-
// colored pulse dots; honest empty state.

import { motion } from 'framer-motion';
import { CheckCheck, Mail, MessageSquare, Video, type LucideIcon } from 'lucide-react';
import PulseDot from '@/components/shared/PulseDot';
import { relativeTime, type ActivityEvent } from '@/utils/overviewQueries';

const KIND_META: Record<
  ActivityEvent['kind'],
  { icon: LucideIcon; dot: 'green' | 'amber' | 'blue' | 'red'; kindLabel: string }
> = {
  email: { icon: Mail, dot: 'green', kindLabel: 'Email captured' },
  message: { icon: MessageSquare, dot: 'blue', kindLabel: 'Message captured' },
  meeting: { icon: Video, dot: 'amber', kindLabel: 'Meeting captured' },
  decision: { icon: CheckCheck, dot: 'green', kindLabel: 'You decided' },
};

export default function ActivityPulse({
  events,
  now,
}: {
  events: ActivityEvent[];
  now: Date;
}): JSX.Element {
  if (events.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        Nothing captured yet — activity appears here the moment a channel flows in.
      </p>
    );
  }
  return (
    <ul className="space-y-1" aria-label="Recent activity">
      {events.map((event, i) => {
        const meta = KIND_META[event.kind];
        const Icon = meta.icon;
        return (
          <motion.li
            key={event.id}
            className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/60 transition-colors"
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.05 * i, ease: 'easeOut' }}
          >
            <span className="neo-chip w-8 h-8 flex items-center justify-center shrink-0">
              <Icon className="w-3.5 h-3.5 text-forest/70" aria-hidden />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] text-foreground truncate leading-snug">{event.title}</p>
              <p className="text-[11px] text-muted-foreground truncate">
                {meta.kindLabel} · {event.detail}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[11px] font-mono text-muted-foreground">
                {relativeTime(event.atIso, now)}
              </span>
              <PulseDot color={meta.dot} size="sm" />
            </div>
          </motion.li>
        );
      })}
    </ul>
  );
}
